import { isJust, just, nothing, type Maybe } from "./maybe";
import { PROJECT_ASSETS_DIR } from "./zwConfig";
import { isTauri } from "./runtime";
import { projectAssetSrc } from "./tauri/assets";

/** Parent folder where an Assets/ directory should live. */
export function assetStorageRoot(
  _mode: "project",
  rootPath: Maybe<string>,
  _activePath: Maybe<string>,
): Maybe<string> {
  return rootPath;
}

export function assetNameFromSource(sourcePath: string): string {
  const base = sourcePath.split(/[/\\]/).pop() ?? "image.png";
  const sanitized = base
    .replace(/[^\w.-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/\.{2,}/g, ".");
  if (
    sanitized === null ||
    sanitized.length === 0 ||
    sanitized === "." ||
    sanitized === ".."
  )
    return "image.png";
  return sanitized;
}

export function normalizeAssetSrc(src: string): string {
  return src
    .replace(/\\/g, "/")
    .trim()
    .replace(/^\.\/+/, "");
}

export function isProjectAssetReference(src: string): boolean {
  const normalized = normalizeAssetSrc(src);
  return (
    normalized.startsWith(`${PROJECT_ASSETS_DIR}/`) &&
    normalized.includes("..") === false
  );
}

/** Extract `Assets/...` from a Tauri asset preview URL. */
export function assetRelativePathFromPreviewUrl(
  storageRoot: string,
  previewSrc: string,
): Maybe<string> {
  const root = storageRoot.replace(/\\/g, "/").replace(/\/+$/, "");
  const assetsPrefix = `${PROJECT_ASSETS_DIR}/`;

  try {
    const url = new URL(previewSrc);
    const path = decodeURIComponent(url.pathname);
    const rootIdx = path.indexOf(root);
    if (rootIdx >= 0) {
      const rel = path.slice(rootIdx + root.length).replace(/^\/+/, "");
      if (isProjectAssetReference(rel)) return just(rel);
    }
    const assetsIdx = path.indexOf(`/${assetsPrefix}`);
    if (assetsIdx >= 0) {
      const rel = path.slice(assetsIdx + 1);
      if (isProjectAssetReference(rel)) return just(rel);
    }
    const lower = path.toLowerCase();
    const marker = `/${assetsPrefix.toLowerCase()}`;
    const idx = lower.indexOf(marker);
    if (idx >= 0) {
      const rel = path.slice(idx + 1);
      if (isProjectAssetReference(rel)) return just(rel);
    }
  } catch {
    // previewSrc may not be a URL
  }

  const normalizedPreview = previewSrc.replace(/\\/g, "/");
  const rootIdx = normalizedPreview.indexOf(root);
  if (rootIdx >= 0) {
    const tail =
      normalizedPreview
        .slice(rootIdx + root.length)
        .replace(/^\/+/, "")
        .split(/[?#]/)[0] ?? "";
    if (isProjectAssetReference(tail)) return just(tail);
  }

  return nothing;
}

/** Best relative asset path for a preview widget + image src. */
export function resolveManagedAssetPath(
  storageRoot: Maybe<string>,
  imageSrc: string,
  previewSrc: Maybe<string> = nothing,
): Maybe<string> {
  const normalized = normalizeAssetSrc(imageSrc);
  if (isProjectAssetReference(normalized)) return just(normalized);
  if (
    isJust(storageRoot) &&
    storageRoot.value.length > 0 &&
    isJust(previewSrc) &&
    previewSrc.value.length > 0
  ) {
    return assetRelativePathFromPreviewUrl(storageRoot.value, previewSrc.value);
  }
  return nothing;
}

export function formatErrorMessage(error: unknown, fallback: string): string {
  if (
    error instanceof Error &&
    error.message !== null &&
    error.message.length > 0
  )
    return error.message;
  if (
    typeof error === "string" &&
    error.trim() !== null &&
    error.trim().length > 0
  )
    return error;
  return fallback;
}

/** Resolved preview URL for a project asset (optional cache-bust after edits). */
export function projectAssetPreviewSrc(
  storageRoot: string,
  relativePath: string,
  cacheBust?: number,
): string {
  const rel = normalizeAssetSrc(relativePath);
  let src = isTauri()
    ? projectAssetSrc(storageRoot, rel)
    : `${storageRoot.replace(/[/\\]+$/, "").replace(/\\/g, "/")}/${rel}`;
  if (typeof cacheBust === "number" && cacheBust !== 0 && cacheBust > 0) {
    src += `${src.includes("?") ? "&" : "?"}v=${cacheBust}`;
  }
  return src;
}

export function assetRelativePath(imported: string): string {
  return imported.replace(/\\/g, "/");
}
